import DataTable, { TableColumn } from 'react-data-table-component';
import { tableHelper } from 'common/helpers/table/table-helper';
import { useTranslation } from 'react-i18next';
import en from './i18n/en-us.json';
import pt from './i18n/pt-br.json';
import es from './i18n/es-es.json';
import { Department, Step as Model, NotificationChannel, NotificationType, NotificationTypeSettings, UpsertUserNotificationSettings, UpsertUserStepNotificationSettings, UserNotificationSettings } from './model/step';
import { useEffect, useState } from 'react';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { StepFilter } from './components/StepFilter';
import { DepartmentSelectorService as DepartmentService } from 'app/department-selector/services/department-selector.service';
import { NotificationService } from './services/notification.service';
import { Constants } from './constants/constants';
import { useFormik } from 'formik';
import { toast } from "react-toastify";
import SaveButton from 'common/partials/SaveButton';
import i18next from 'i18next';
import { ConfirmUnsavedModal } from './components/ConfirmUnsavedModal';

export interface Props {
    userId: number
}

interface Search {
    term: string,
    departmentId?: number
}

type Channels = {
    [k in NotificationChannel]: boolean
}

type StepNotificationTypes = {
    [stepId: number]: NotificationTypes
}

type NotificationTypes = {
    [k in NotificationType]: boolean
}

const defaultChannels: Channels = {
    [NotificationChannel.App]: false,
    [NotificationChannel.Email]: false,
    [NotificationChannel.Teams]: false,
    [NotificationChannel.None]: false,
}

const defaultNotificationTypes: NotificationTypes = {
    [NotificationType.None]: false,
    [NotificationType.Sucess]: false,
    [NotificationType.Critical]: false
}


interface FormContract {
    channelSettings: Channels,
    stepNotificationTypes: StepNotificationTypes,
    autoCreateSubscriptionEnabled: boolean
}

export function NotificationCenter(props: Props) {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const [search, setSearchTerm] = useState<Search>({ term: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [departments, setDepartaments] = useState<Department[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalRows, setTotalRows] = useState(0);
    const [sorting, setSorting] = useState<{ orderBy?: string; order?: 'asc' | 'desc' }>({});
    const [filteredTableData, setFilteredTableData] = useState<Model[]>([]);
    const [showSureModal, setShowSureModal] = useState<boolean>(false);
    const [sureModalAction, setSureModalAction] = useState<{ page: number, rowsPerPage: number }>({ page: 1, rowsPerPage: 10 });
    const [enabledChannels, setEnabledChannels] = useState<NotificationChannel[]>(Object.values(NotificationChannel).filter(e => +e) as NotificationChannel[]);
    const [hasDisabledChannels, setHasDisabledChannels] = useState(false);

    const fetchTenantEnabledChannels = async () => {
        try {
            setIsLoading(true);
            const response = await NotificationService.getTenantEnabledChannels();

            if (response == null || response.hasErrors) return;

            const channels = response.data.enabledChannels
                .map(n => +n)
                .filter(n => Object.values(NotificationChannel).includes(n)) as NotificationChannel[];

            setEnabledChannels(channels);
            setHasDisabledChannels(channels.length < Object.values(NotificationChannel).filter(e => +e).length);
        }
        catch (e) {
            toast.error(`${t('crud.read.errorMessage')}`);
        }
        finally {
            setIsLoading(false);
        }
    }

    const fetchUserNotificationSettings = async () => {
        try {
            setIsLoading(true);
            const response = await NotificationService.getUserNotificationSettings(props.userId);
            if (response == null || response.hasErrors) return;

            const channels = response.data.channels.reduce((p, c) => {
                p[c] = true;
                return p;
            }, { ...defaultChannels });
            formik.setFieldValue('channelSettings', channels);
            formik.setFieldValue('autoCreateSubscriptionEnabled', response.data.autoCreateSubscriptionEnabled);
        }
        catch (e) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally { setIsLoading(false) }
    }

    const fetchDepartaments = async () => {
        try {
            setIsLoading(true);
            const response = await DepartmentService.getUserAndDepartments(props.userId);

            if (response != null && !response.hasErrors) {
                setDepartaments(response.data.departments);
            }
        }
        catch (e) {
            toast.error(`${t('crud.read.errorMessage')}`);
        }
        finally {
            setIsLoading(false);
        }
    }

    const fetchSteps = async () => {
        try {
            setIsLoading(true);
            const query = {
                pageNumber: currentPage,
                pageSize: rowsPerPage,
                order: sorting.order,
                orderBy: sorting.orderBy,
                term: search.term,
                departmentId: search.departmentId
            };
            const response = await NotificationService.getUserStepSettings(props.userId, query);
            if (response == null || response.hasErrors) return;

            const stepNotificationTypes = response.data.items.reduce<StepNotificationTypes>((p, c) => {
                p[c.id] = c.notifications.reduce<NotificationTypes>((pv, cr) => {
                    pv[cr.type] = cr.on;
                    return pv;
                }, { ...defaultNotificationTypes });
                return p;
            }, {});

            formik.setFieldValue('stepNotificationTypes', stepNotificationTypes);
            formik.setTouched({});
            setFilteredTableData(response.data.items);
            setTotalRows(response.data.totalCount);
        }
        catch (e) {
            toast.error(`${t('crud.read.errorMessage')}`);
        }
        finally {
            setIsLoading(false);
        }
    }

    const postNotificationSettings = async () => {
        try {
            const form = formik.values;
            const channels: NotificationChannel[] = Object.entries(form.channelSettings).filter(([_, on]) => on).map(([channel, _]) => +channel);
            const stepSettings = Object.entries(form.stepNotificationTypes).map(([stepId, notifications]) => {
                return {
                    stepId: +stepId,
                    notifications: Object.entries(notifications).map(([n, on]) => {
                        return {
                            on: on,
                            type: +n
                        } as NotificationTypeSettings;
                    })
                }
            });

            const body: UpsertUserNotificationSettings = {
                userId: props.userId,
                channels: channels,
                autoCreateSubscriptionEnabled: form.autoCreateSubscriptionEnabled,
                stepNotifications: stepSettings
            }

            setIsLoading(true);
            const response = await NotificationService.upsertUserNotificationSettings(body);

            if (response == null || response.hasErrors) return;

            await fetchUserNotificationSettings();
            await fetchSteps();

            toast.success(`${t('crud.create.successMessage')}`);
        }
        catch (e) {
            toast.error(`${t('crud.create.errorMessage')}`);
        }
        finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchDepartaments();
        fetchUserNotificationSettings();
        fetchTenantEnabledChannels();
    }, []);

    useEffect(() => {
        fetchSteps();
    }, [search, currentPage, rowsPerPage]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            channelSettings: defaultChannels,
            stepNotificationTypes: {},
            autoCreateSubscriptionEnabled: false
        } as FormContract,
        onSubmit: postNotificationSettings
    });

    const toggleNotificationSettings = (stepId: number, type: NotificationType) => {
        const key = `stepNotificationTypes.${stepId}.${type}`;
        formik.setTouched(formik.values);
        formik.setFieldValue(key, !formik.values.stepNotificationTypes[stepId][type])
    }

    const updateRowsPerPage = (newPerPage: number, newPage: number) => {
        if (formik.touched['stepNotificationTypes']) {
            setSureModalAction({ page: newPage, rowsPerPage: newPerPage });
            setShowSureModal(true);
            return;
        }
        setRowsPerPage(newPerPage);
        setCurrentPage(newPage);
    }

    const updatePage = (newPage: number) => {
        if (formik.touched['stepNotificationTypes']) {
            setSureModalAction({ page: newPage, rowsPerPage: rowsPerPage });
            setShowSureModal(true);
            return;
        }
        setCurrentPage(newPage);
    }

    const columns: TableColumn<Model>[] = [
        {
            name: t('notificationCenter.columns.id'),
            selector: (row) => row?.id || '',
            sortable: true,
            sortField: 'Id',
            width: '50px'
        },
        {
            name: t('notificationCenter.columns.name'),
            selector: (row) => row.name || '',
            sortable: true,
            sortField: 'Name'
        },
        {
            name: t('notificationCenter.columns.departments'),
            selector: (row) => row.departments.map(r => r.name).join(', ') || '',
            sortable: true,
            sortField: 'UserName'
        },
        {
            name: t('notificationCenter.columns.notifications'),
            cell: (row) => {
                const types = formik.values.stepNotificationTypes[row.id] || {};
                return types &&
                    <div className="btn-group" role="group" style={{ minWidth: 0 }}>
                        <button
                            type="button"
                            name={NotificationType.Sucess.toString()}
                            onClick={() => toggleNotificationSettings(row.id, NotificationType.Sucess)}
                            className={`btn btn-sm btn-${types[NotificationType.Sucess] ? 'primary' : 'secondary'}`}
                        >
                            {t("notificationCenter.columns.success")}
                        </button>
                        <button
                            type="button"
                            name={NotificationType.Sucess.toString()}
                            onClick={() => toggleNotificationSettings(row.id, NotificationType.Critical)}
                            className={`btn btn-sm btn-${types[NotificationType.Critical] ? 'primary' : 'secondary'}`}
                        >
                            {t("notificationCenter.columns.critical")}
                        </button>
                    </div>
            },
            width: '200px'
        },
    ];

    return <>
        <div className="card">
            <div className="card-body">
                <div className="row">
                    <div className="col-12">
                        <div className="card w-100 shadow-none">
                            <div className="pt-0">
                                <h4>{t("notificationCenter.title")}</h4>
                                <form className="form" onSubmit={formik.handleSubmit}>
                                    <label className='form-label mt-5'>{t('notificationCenter.autoSubscription')}</label>
                                    <div className="row">
                                        <div className="col-2 d-flex align-items-center">
                                            <div className="form-check form-check-solid form-switch">
                                                <input
                                                    id='autoCreateSubscriptionEnabled'
                                                    className="form-check-input w-45px h-30px" type="checkbox"
                                                    checked={formik.values.autoCreateSubscriptionEnabled}
                                                    onChange={(e) => formik.setFieldValue(`autoCreateSubscriptionEnabled`, e.target.checked)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <label className='form-label mt-5'>{t("notificationCenter.channelsLabel")}</label>
                                    <div className="row">
                                        {
                                            Object.values(NotificationChannel).filter(e => +e).map(c => c as NotificationChannel).map((c) =>
                                                <div key={c.toString()} className="col-2 d-flex align-items-center">
                                                    <div className="form-check form-check-solid form-switch">
                                                        <input
                                                            id={c.toString()}
                                                            className="form-check-input w-45px h-30px" type="checkbox"
                                                            checked={formik.values.channelSettings[c]}
                                                            onChange={(e) => formik.setFieldValue(`channelSettings.${c}`, e.target.checked)}
                                                            disabled={!enabledChannels.includes(c)}
                                                        />
                                                    </div>
                                                    <label className='form-label mt-3' htmlFor={c.toString()}>
                                                        <span>{Constants.ChannelIcon[c]}</span>
                                                        <span className="ms-2">{t(`channels.${NotificationChannel[c].toLocaleLowerCase()}`)}</span>
                                                    </label>
                                                </div>
                                            )
                                        }
                                        {hasDisabledChannels && (
                                            <div>
                                                <label className='text-muted mt-5'>{t("notificationCenter.disabledChannelsMessage")}</label>
                                            </div>
                                        )}
                                    </div>

                                    <hr className="mt-5" />
                                    <label className='form-label mt-5'>{t("notificationCenter.stepNotificationsLabel")}</label>
                                    <DataTable
                                        title={
                                            <StepFilter
                                                departments={departments}
                                                onFilter={(e) => setSearchTerm({ ...search, departmentId: e?.id || undefined })}
                                                onSearch={(e) => setSearchTerm({ ...search, term: e })}
                                            />
                                        }
                                        persistTableHead={true}
                                        columns={columns}
                                        data={filteredTableData}
                                        noDataComponent={tableHelper.NoDataComponent()}
                                        dense
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                                        paginationPerPage={rowsPerPage}
                                        paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                                        onChangePage={updatePage}
                                        onChangeRowsPerPage={updateRowsPerPage}
                                        highlightOnHover
                                        progressPending={isLoading}
                                        progressComponent={tableHelper.Loader()}
                                        contextMessage={tableHelper.ContextMessage()}
                                        /*onSelectedRowsChange={handleRowSelected}*/
                                        /*clearSelectedRows={toggleCleared}*/
                                        selectableRowsVisibleOnly={true}
                                        customStyles={itLabTableStyle}
                                        //onSort={handleListSort}
                                        sortServer
                                    />
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div >

            <div className="card-footer d-flex justify-content-end py-6 px-9">
                <SaveButton type="submit" loading={isLoading} onClick={formik.handleSubmit} />
            </div>
        </div >

        <ConfirmUnsavedModal action={sureModalAction} isOpen={showSureModal} onCancel={() => setShowSureModal(false)}
            onConfirm={(action) => {
                setCurrentPage(action.page);
                setRowsPerPage(action.rowsPerPage);
                setShowSureModal(false);
            }}
        />
    </>
}